import { Component, AfterViewInit, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { AuthorizationService, User } from '@shared/authorization.service';
import { pinClear, pinGetter } from '@shared/fit-api-client.service';
import { FitConfigProviderService, SidebarNavigationItem } from '@shared/fit-config-provider.service';
import { FreeTrialService } from '@shared/trieal-period.service';
import { PageMetaService } from '@shared/page-meta.service';
import { TranslationService } from '@shared/translation.service';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MessagingService } from './messaging.service';
import { RecordingService } from './recording/recording.service';

import { 
    AllApplicationsUnreadMessages, 
    MessageData, 
    NotificationMessage, 
    ResolutionCenterService, 
    UnreadNotifications 
} from './resolution-center/resolution-center.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})

export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
    constructor(
        private recording: RecordingService, //recording must be instantiated even if it's not used
        private messagingService: MessagingService,
        private freeTrialService: FreeTrialService,
        private resolutionService: ResolutionCenterService,
        private pageMetaService: PageMetaService,
        private auth: AuthorizationService,
        private fitConfigProviderService: FitConfigProviderService,
        private translationService: TranslationService,
        private router: Router
    ) {}

    public isEliteRugbyApp = location.hostname === 'cms.eliterugbyapp.com';
    public isHyperSystem = location.hostname === 'cms.hypersystemapp.com';

    public freeTrialEndedAgo: number;
    public isCollapsed = false;
    public isTabletMobile = false;
    public pin = pinGetter();
    public isStreamingPage = false;
    public isBuilderPage = false;
    public contentLayoutInstant = false;
    public resolutionMessages = 0;
    public outsideClick = false;
    public clientMessages = 0;
    public showNotificationList = false;
    public trialEndsIn;
    public allAppsUnreadMessages: AllApplicationsUnreadMessages[] = [];
    public nothIconFarLeft = false;
    public currentMessage = new BehaviorSubject<MessageData>(null);
    public isOnBoardingPage = false;
    public loading = false;

    get isAuthenticated() {
        this.pin = pinGetter();
        return this.auth.isAuthenticated();
    }

    get user() { return this.auth.user; }
    get navigation() { return this.fitConfigProviderService.navigation; }
    get isRoot() { return this.fitConfigProviderService.isRoot; }
    get isOwner() { return this.fitConfigProviderService.isOwner; }
    get isAdministrator() { return this.fitConfigProviderService.isAdministrator; }
    get isCoach() { return this.fitConfigProviderService.isCoach; }
    get totalNotifications() { return this._totalNotifications; }
    set totalNotifications(value) { this._totalNotifications = value; }
    get showSpinner() { return this.auth.showSpinner; }

    @ViewChild('notifications') nothIcon: ElementRef;

    public ngOnInit() {
        this.isTabletMobile = window.innerWidth < 992;
        this.isCollapsed = this.isTabletMobile ? true : false;
        this.setPageMeta();
        if (this.auth.isAuthenticated()) {
            this.translationService.initializeAfterLogin();
        }

        this.updatePageLayoutFlags(this.router.url);

        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.updatePageLayoutFlags(event.urlAfterRedirects);
            });

        /**
         * When one of the messaging components (client center, resolution center etc.) signals 
         * that notifications may have changed (e.g. after sending a message, marking as seen), 
         * refresh the headernotification count and list after a short delay. 
         * Skipped for root users who rely on getAllAppsMessages() for their notification state.
         */
        this.resolutionService.updateTotalNotifications.subscribe(bool => {
            //if (this.fitConfigProviderService.isOwner) {
            if (!this.fitConfigProviderService.isRoot) {
                setTimeout(() => {
                    this.getUnreadNotifications();
                }, 2000);
            }
        });

        this.user.subscribe(res => {
            this.clearIntervals();

            if (!res) {
                return;
            }

            // Persist FCM token to backend when user is logged in
            this.persistMessagingTokenIfLoggedIn();

            if (res.free_trial_ends_at) {
                const trial = new Date(res.free_trial_ends_at);
                const trialMilliseconds = trial.getTime();
                const now = Date.now();

                this.trialEndsIn = Math.round((trialMilliseconds - now) / (24 * 60 * 60 * 1000));
                if (this.trialEndsIn < 0) {
                    this.freeTrialEndedAgo = Math.abs(this.trialEndsIn);
                }

                this.freeTrialService.freeTrialEndsIn.emit(this.trialEndsIn);
            }

            //clearInterval(this._notificationInterval);

            if (this.fitConfigProviderService.isRoot) {
                //set interval to get resolution center messages
                this._getAllAppsMessagesInterval = setInterval(() => this.getAllAppsMessages(), 60 * 1000);
                this.getAllAppsMessages(); //get messages once immediately
            //} else if (this.fitConfigProviderService.isOwner) {
            } else { //owner, admin, coach
                //set interval to get unread app notifications
                this._getTotalNotificationsInterval = setInterval(() => this.getUnreadNotifications(), 60 * 1000); //fetch notifications every minute in a minute
                this.getUnreadNotifications(); //fetch notifications once immediately
            }
        });
    }

    ngAfterViewInit() {
        if (!('serviceWorker' in navigator)) {
            return;
        }

        this.messagingService.requestPermission().subscribe({
            next: (token: string) => {
                if (!token) {
                    return;
                }

                localStorage.setItem('service-token', token);
                if (this.auth.isAuthenticated()) {
                    this.messagingService.saveToken(token);
                }
            },
            error: (error) => console.error('FCM permission or token error', error),
        });

        this.messagingService.tokenChanged().subscribe(newToken => {
            const prevToken = localStorage.getItem('service-token');
            if (prevToken === newToken) {
                return;
            }
            localStorage.setItem('service-token', newToken);
            if (this.auth.isAuthenticated()) {
                this.messagingService.saveToken(newToken);
            }
        });

        this.messagingService.receiveMessage().subscribe(
            (notification: NotificationMessage) => {
                this.currentMessage.next(notification.data);

                if (this.fitConfigProviderService.isRoot) {
                    this.getAllAppsMessages();
                } else {
                    this.getUnreadNotifications();
                }
            }
        );
    }

    /** Sends the current FCM token to the backend when the user is logged in. */
    private persistMessagingTokenIfLoggedIn() {
        const token = localStorage.getItem('service-token');
        if (token && this.auth.isAuthenticated()) {
            this.messagingService.saveToken(token);
        }
    }

    public getLogoUrl(user: User) {
        return this.pin && user?.wizard_section_status?.app_visuals_2
            ? `${environment.awsS3BucketUrl}/pwa/${this.pin}/assets/app_icons/icon-72x72.png`
            : 'assets/img/logo-sidebar.png';
    }

    public showNotifications() {
        if (this.nothIcon.nativeElement) {
            const el = this.nothIcon.nativeElement;
            const rect = el.getBoundingClientRect();
            
            if (rect.left < 300 && !this.nothIconFarLeft) {
                this.nothIconFarLeft = true;
            } else if (rect.left >= 300 && this.nothIconFarLeft) {
                this.nothIconFarLeft = false;
            }
        }

        this.showNotificationList = !this.showNotificationList;
        if (this.showNotificationList) {
            setTimeout(() => {
                this.outsideClick = true;
            }, 50);
        } else {
            this.outsideClick = false;
        }
    }

    @HostListener('window:click')

    public hideNotificationList() {
        if (this.showNotificationList && this.outsideClick) {
            this.showNotificationList = false;
            this.outsideClick = false;
        }
    }

    public async removeNotifications() {
        this.clearAllNotifications();
        return;
    }

    public async onMarkNotificationSeen(id: number) {
        const notification = this.totalNotifications.notifications
            ?.find(noth => noth.notification_id === id);

        // If notification is not found or already seen, do nothing
        if (!notification || notification.seen) {
            return;
        }

        const { error } = await this.resolutionService.markNotificationSeen(id);
        if (!error) {
            notification.seen = true;
            if (this.totalNotifications.count > 0) {
                this.totalNotifications.count--;
            }
        }
    }

    public async clearAllNotifications() {
        const { data, error } = await this.resolutionService.clearAllNotifications();
        if (!error) {
            this.totalNotifications.count = 0;
            this.totalNotifications.notifications = [];
        }
    }

    public async getUnreadNotifications() {
        const { data, error } = await this.resolutionService.getUnreadNotifications();
        if (!error) {
            /*
            data.notifications.forEach(noth => {
                noth.seen = false;
            });
            */
            this.totalNotifications = data;
        }
    }

    public ngOnDestroy() {
        this.clearIntervals();
    }

    public collapseNav() {
        if (this.isTabletMobile) {
            this.isCollapsed = true;
        }

        const firstLevel = document.querySelector('.first-level-items');
        const parent = firstLevel.previousElementSibling;
        parent.classList.remove('active');
        firstLevel.children[0].classList.remove('active');
    }

    public collapseOnTabletMobile() {
        this.clearIntervals();

        if (this.isTabletMobile) {
            this.isCollapsed = true;
        }
    }

    public closeAllTabs() {
        this.navigation.forEach(nav => {
            if (nav.opened) {
                nav.opened = false;
                if (nav.items.length) {
                    nav.items.forEach(item => {
                        if (item.opened) {
                            item.opened = false;
                        }
                    });
                }
            }
        });
    }

    public switchOpenedTab(navIndex: number, navigation: any) {
        navigation.forEach((item, index) => {
            if (index === navIndex) {
                return navigation[index].opened = !navigation[index].opened;
            }
            return item.opened = false;
        });

        setTimeout(() => {
            this.previewNavigation();
        }, 100);

    }

    public previewNavigation() {
        const firstLevel = document.querySelector('.first-level-items');
        const parent = firstLevel.previousElementSibling;
        const seconditems = document.querySelector('.second-level-items');

        if (!location.href.includes('preview')) {
            return;
        }

        if (!firstLevel.classList.contains('opened')) {
            if (!parent.classList.contains('active')) {
                parent.classList.add('active');
            }
        }

        if (!seconditems.classList.contains('opened')) {
            if (!firstLevel.children[0].classList.contains('active')) {
                firstLevel.children[0].classList.add('active');
            }
        }
    }

    public isChildActive(openedNav: boolean, navItems?: SidebarNavigationItem[]) {
        if (!navItems) return false;
        
        if (!openedNav) {
            return navItems.some(item => {
                if (item && item.items) {
                    return item.items.some(i => {
                        if (i && i.items) {
                            return i.items.some(ii => {
                                if (ii && ii.items) {
                                    return ii.items.some(iii => this.router.isActive(iii.url,
                                        { paths: 'subset', queryParams: 'exact', fragment: 'ignored', matrixParams: 'ignored' }));
                                }
                                return this.router.isActive(ii.url,
                                    { paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' });
                            });
                        }
                        return this.router.isActive(i.url,
                            { paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' });
                    });
                }
                return this.router.isActive(item.url,
                    { paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' });
            });
        }
    }

    public async logout() {
        pinClear();
        await this.auth.logout();
        this.clearIntervals();
    }

    private setPageMeta() {
        if (this.isEliteRugbyApp) {
            this.pageMetaService.setPageMeta('Elite Rugby App CMS', 'elite_rugby_app');
        } else if (this.isHyperSystem) {
            this.pageMetaService.setPageMeta('Hyper System CMS', 'hyper_system');
        } else {
            this.pageMetaService.setPageMeta('Lean On CMS', 'default');
        }
    }

    private async getAllAppsMessages() {
        if (!this.auth.isAuthenticated()) {
            this.clearIntervals();
            return;
        }

        if (!this.fitConfigProviderService.isRoot || this.loading) {
            return;
        }

        this.loading = true;

        const { data, error } = await this.resolutionService.getAllAplicationsUnreadMessages();
        if (!error) {
            this.allAppsUnreadMessages = data;
            this.resolutionService.newDetailsResolutionCenterMessage.next(data);
        }

        this.loading = false;
    }

    private clearIntervals() {
        if (this._getAllAppsMessagesInterval !== null) {
            clearInterval(this._getAllAppsMessagesInterval);
            console.log('clearInterval getAllAppsMessages');
            this._getAllAppsMessagesInterval = null;
        }
        
        if (this._getTotalNotificationsInterval !== null) {
            clearInterval(this._getTotalNotificationsInterval);
            console.log('clearInterval getTotalNotifications');
            this._getTotalNotificationsInterval = null;
        }
    }

    private updatePageLayoutFlags(url: string): void {
        const path = url.split('?')[0];
        const isBuilder = path.includes('/training/build/')
            || path.includes('/training/build-session/')
            || path.includes('/program/build/');

        if (this.isBuilderPage && !isBuilder) {
            this.contentLayoutInstant = true;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.contentLayoutInstant = false;
                });
            });
        }

        this.isStreamingPage = path.includes('live-events/streaming');
        this.isOnBoardingPage = path.includes('profile/onboarding-wizard');
        this.isBuilderPage = isBuilder;
    }

    private _getAllAppsMessagesInterval = null;
    private _getTotalNotificationsInterval = null;
    private _totalNotifications: UnreadNotifications = {
        count: 0,
        notifications: []
    };
}
